import { type Box3, Euler, type Object3D, Quaternion, Vector3 } from 'three';
import type { CameraMode } from '../camera/cameraMode';
import type { MorningShaft } from '../atmosphere/morningShaft';
import type { HeroEntry, HeroPlacement } from '../loaders/heroLoader';
import type { CullSettings } from '../scene/wallCull';
import type { AudioCue } from '../audio/cues';
import type { SoundPlacement, Variation, Mod } from '../audio/soundscape';
import { type SoundscapeDoc, PLACEMENT_KEYS, CUE_KEYS } from '../audio/soundscapeStore';
import type { AudioAsset } from '../audio/manifest';

// All three save flows share a few invariants:
//   - dev middleware writes the body verbatim (after JSON.parse validation),
//     so the client owns formatting. Custom formatters below preserve the
//     hand-curated layout that was checked into git.
//   - 3dp rounding everywhere — matches the existing files and keeps git
//     diffs small.

// 3dp rounding, finite-safe. A non-finite value (NaN/Infinity — e.g. an emptied
// numeric field committed via parseFloat('') → NaN, or a divide that went bad
// mid-edit) used to pass straight through and be interpolated as a bare `NaN`
// token into the hand-rolled JSON, which is INVALID json: the dev-server's
// JSON.parse then 400'd the whole write and the save surfaced as "save ✕"
// ("soundscape failed") — one corrupted number losing the entire save. Coercing
// to 0 keeps the document valid so everything else persists; the offending field
// reads as 0 on reload (visible + fixable) instead of silently dropping all work.
const r3 = (n: number): number => {
  const r = Math.round(n * 1000) / 1000;
  return Number.isFinite(r) ? r : 0;
};
const r3v = (v: Vector3): [number, number, number] => [r3(v.x), r3(v.y), r3(v.z)];

// DM5 — a cheap, dependency-free string hash for the CLIENT-side "changed on
// disk since our last save" conflict guard (no server middleware, no crypto
// import). Deterministic + fast; only ever compared against itself.
const cheapHash = (s: string): string => {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 0x01000193); }
  return (h >>> 0).toString(16);
};

// ── manifest.json ────────────────────────────────────────────────────────
// Custom formatter so short placements + numeric arrays stay on one line.
// JSON.stringify(_, _, 2) explodes them into multi-line blocks and bloats
// the file vs the hand-curated layout.
const formatScalar = (v: unknown): string => JSON.stringify(v);
const formatNumArray = (arr: number[]): string => `[${arr.join(', ')}]`;
const formatPlacement = (p: HeroPlacement): string => {
  const parts: string[] = [`"state": ${formatScalar(p.state)}`];
  if (p.position) parts.push(`"position": ${formatNumArray(p.position)}`);
  if (p.rotation) parts.push(`"rotation": ${formatNumArray(p.rotation)}`);
  if (p.scale !== undefined) {
    parts.push(`"scale": ${Array.isArray(p.scale) ? formatNumArray(p.scale) : p.scale}`);
  }
  if (p.material_variant !== undefined) parts.push(`"material_variant": ${formatScalar(p.material_variant)}`);
  if (p.visible !== undefined) parts.push(`"visible": ${p.visible}`);
  let hasUserData = false;
  if (p.userData) {
    hasUserData = true;
    const ud = Object.entries(p.userData)
      .map(([k, v]) => `${formatScalar(k)}: ${formatScalar(v)}`)
      .join(', ');
    parts.push(`"userData": { ${ud} }`);
  }
  const oneLine = `{ ${parts.join(', ')} }`;
  // Placements with userData are conceptually richer — keep them expanded
  // for readability even if they'd fit on one line.
  if (!hasUserData && oneLine.length <= 110) return oneLine;
  return '{\n          ' + parts.join(',\n          ') + '\n        }';
};
const formatHero = (entry: HeroEntry): string => {
  const lines: string[] = [];
  lines.push(`      "id": ${formatScalar(entry.id)},`);
  lines.push(`      "url": ${formatScalar(entry.url)},`);
  if (entry.interactive !== undefined) {
    lines.push(`      "interactive": ${entry.interactive},`);
  }
  const placements = (entry.placements ?? []).map((p) => '        ' + formatPlacement(p)).join(',\n');
  lines.push('      "placements": [\n' + placements + '\n      ]');
  return '    {\n' + lines.join('\n') + '\n    }';
};
const formatManifest = (manifest: { heroes: HeroEntry[] }): string => {
  const heroes = (manifest.heroes ?? []).map(formatHero).join(',\n');
  return '{\n  "heroes": [\n' + heroes + '\n  ]\n}\n';
};

// ── camera positions.json ────────────────────────────────────────────────
export interface CameraSavePayload {
  exterior: { position: [number, number, number]; target: [number, number, number] };
  doorway: [number, number, number];
  interiorAABB: { min: [number, number, number]; max: [number, number, number] };
  cull: { offset: number; enabled: boolean };
  tunables: Record<string, Record<string, number>>;
  // DM13 — bookmarks transcend eras (no `morph`); `anchor` marks a hero-local pose.
  bookmarks: Record<string, { position: [number, number, number]; target: [number, number, number]; t?: number; anchor?: string }>;
}

const formatCameraPositions = (data: CameraSavePayload): string => {
  const ex = data.exterior;
  const ai = data.interiorAABB;
  const tunableLines = Object.entries(data.tunables).map(([name, vals]) => {
    const pairs = Object.entries(vals).map(([k, v]) => `"${k}": ${v}`).join(', ');
    return `    "${name}": { ${pairs} }`;
  });
  const bookmarkEntries = Object.entries(data.bookmarks);
  const bookmarkLines = bookmarkEntries.map(([name, b]) => {
    const parts = [`"position": [${b.position.join(', ')}]`, `"target": [${b.target.join(', ')}]`];
    if (typeof b.t === 'number') parts.push(`"t": ${r3(b.t)}`);
    // DM13 — `morph` is no longer written (bookmarks transcend eras); a save
    // strips it from any legacy entry. `anchor` records hero-local framing.
    if (b.anchor) parts.push(`"anchor": ${JSON.stringify(b.anchor)}`);
    return `    ${JSON.stringify(name)}: { ${parts.join(', ')} }`;
  });
  // Bookmarks: keep the key out of the file entirely when empty so the
  // schema reads cleanly for first-time users until they save one.
  const bookmarksBlock = bookmarkEntries.length
    ? ',\n  "bookmarks": {\n' + bookmarkLines.join(',\n') + '\n  }'
    : '';
  return (
    '{\n' +
    '  "exterior": {\n' +
    `    "position": [${ex.position.join(', ')}],\n` +
    `    "target": [${ex.target.join(', ')}]\n` +
    '  },\n' +
    `  "doorway": [${data.doorway.join(', ')}],\n` +
    '  "interiorAABB": {\n' +
    `    "min": [${ai.min.join(', ')}],\n` +
    `    "max": [${ai.max.join(', ')}]\n` +
    '  },\n' +
    `  "cull": { "offset": ${data.cull.offset}, "enabled": ${data.cull.enabled} },\n` +
    '  "tunables": {\n' +
    tunableLines.join(',\n') + '\n' +
    '  }' +
    bookmarksBlock + '\n' +
    '}\n'
  );
};

// Variation block — compact, inline (omits unset keys so diffs stay minimal).
const formatVary = (v: Variation): string => {
  const parts: string[] = [];
  if (v.chance !== undefined) parts.push(`"chance": ${r3(v.chance)}`);
  if (v.oneOf) parts.push(`"oneOf": ${JSON.stringify(v.oneOf)}`);
  if (v.detune !== undefined) parts.push(`"detune": ${v.detune}`);
  if (v.jitterSec !== undefined) parts.push(`"jitterSec": ${v.jitterSec}`);
  return `{ ${parts.join(', ')} }`;
};
const formatMod = (m: Mod): string => {
  const parts: string[] = [];
  if (m.depth !== undefined) parts.push(`"depth": ${r3(m.depth)}`);
  if (m.rateSec !== undefined) parts.push(`"rateSec": ${m.rateSec}`);
  if (m.reshuffleSec !== undefined) parts.push(`"reshuffleSec": ${m.reshuffleSec}`);
  return `{ ${parts.join(', ')} }`;
};

// ── cue formatting (one cue per line; shared by soundscape.json below) ─────
// `t` rounded to 3dp to match every other persisted file + keep git diffs tiny.
const formatAudioCue = (c: AudioCue): string => {
  const parts: string[] = [`"id": ${JSON.stringify(c.id)}`, `"t": ${r3(c.t)}`];
  if (c.state) parts.push(`"state": ${JSON.stringify(c.state)}`);
  if (c.label) parts.push(`"label": ${JSON.stringify(c.label)}`);
  parts.push(`"actions": ${JSON.stringify(c.actions)}`);
  if (c.vary) parts.push(`"vary": ${formatVary(c.vary)}`);
  for (const k of Object.keys(c)) {
    if (CUE_KEYS.has(k)) continue;
    const v = (c as unknown as Record<string, unknown>)[k];
    if (v === undefined) continue; // JSON.stringify(undefined) → "undefined" (invalid JSON → 400)
    parts.push(`${JSON.stringify(k)}: ${JSON.stringify(v)}`);
  }
  return `    { ${parts.join(', ')} }`;
};

// ── soundscape.json (unified: placements + cues) ───────────────────────────
// One placement per line, one cue per line — same legible, diff-friendly shape
// as the legacy files this merges. Key order matches the hand-seeded file so a
// save round-trip is a no-op diff.
const formatPlacementAt = (at: NonNullable<SoundPlacement['at']>): string => {
  const parts: string[] = [];
  if (at.heroId) parts.push(`"heroId": ${JSON.stringify(at.heroId)}`);
  if (at.position) parts.push(`"position": [${at.position.map(r3).join(', ')}]`);
  return `{ ${parts.join(', ')} }`;
};
const formatSoundPlacement = (p: SoundPlacement): string => {
  const parts: string[] = [
    `"id": ${JSON.stringify(p.id)}`,
    `"kind": ${JSON.stringify(p.kind)}`,
  ];
  // asset is optional since A12 (a bed may be a pure drop population).
  if (p.asset !== undefined) parts.push(`"asset": ${JSON.stringify(p.asset)}`);
  parts.push(`"scope": ${JSON.stringify(p.scope)}`, `"channel": ${JSON.stringify(p.channel)}`);
  // Clip region + trim (N1): t in day units, dur/in/out/fades in real seconds.
  if (p.t !== undefined) parts.push(`"t": ${r3(p.t)}`);
  if (p.dur !== undefined) parts.push(`"dur": ${r3(p.dur)}`);
  if (p.playback !== undefined) parts.push(`"playback": ${JSON.stringify(p.playback)}`); // N10 — sync|trigger
  if (p.arbitration !== undefined) parts.push(`"arbitration": ${JSON.stringify(p.arbitration)}`); // N7
  if (p.in !== undefined) parts.push(`"in": ${r3(p.in)}`);
  if (p.out !== undefined) parts.push(`"out": ${r3(p.out)}`);
  if (p.fadeIn !== undefined) parts.push(`"fadeIn": ${r3(p.fadeIn)}`);
  if (p.fadeOut !== undefined) parts.push(`"fadeOut": ${r3(p.fadeOut)}`);
  if (p.resume !== undefined) parts.push(`"resume": ${JSON.stringify(p.resume)}`); // N4 (music)
  if (p.at && (p.at.heroId || p.at.position)) parts.push(`"at": ${formatPlacementAt(p.at)}`);
  if (p.spatial) parts.push(`"spatial": ${JSON.stringify(p.spatial)}`);
  if (p.refDistance !== undefined) parts.push(`"refDistance": ${r3(p.refDistance)}`);
  if (p.maxDistance !== undefined) parts.push(`"maxDistance": ${r3(p.maxDistance)}`);
  if (p.gain !== undefined) parts.push(`"gain": ${r3(p.gain)}`);
  if (p.span) parts.push(`"span": [${r3(p.span[0])}, ${r3(p.span[1])}]`);
  if (p.loop !== undefined) parts.push(`"loop": ${p.loop}`);
  if (p.vary) parts.push(`"vary": ${formatVary(p.vary)}`);
  if (p.mod) parts.push(`"mod": ${formatMod(p.mod)}`);
  // Drops (A12) — one compact object per member, same legible shape as vary/mod.
  if (p.drops?.length) parts.push(`"drops": [${p.drops.map((d) => JSON.stringify(d)).join(', ')}]`);
  if (p.minSpacingSec !== undefined) parts.push(`"minSpacingSec": ${r3(p.minSpacingSec)}`);
  if (p.requiresPower) parts.push(`"requiresPower": true`);
  // Emit any unknown fields the loader preserved (S2) — forward-compat / hand
  // annotations round-trip instead of being stripped on save.
  for (const k of Object.keys(p)) {
    if (PLACEMENT_KEYS.has(k)) continue;
    const v = (p as unknown as Record<string, unknown>)[k];
    if (v === undefined) continue; // JSON.stringify(undefined) → "undefined" (invalid JSON → 400)
    parts.push(`${JSON.stringify(k)}: ${JSON.stringify(v)}`);
  }
  return `    { ${parts.join(', ')} }`;
};
const formatSoundscape = (doc: SoundscapeDoc): string => {
  // Valid entries get the curated formatter; quarantined raw entries (S2) ride
  // along verbatim (compact JSON) so a typo'd / forward-compat entry survives a
  // save instead of being deleted from disk.
  const pParts = doc.placements.map(formatSoundPlacement);
  for (const d of doc.dropped?.placements ?? []) pParts.push('    ' + JSON.stringify(d));
  const cParts = doc.cues.map(formatAudioCue);
  for (const d of doc.dropped?.cues ?? []) cParts.push('    ' + JSON.stringify(d));
  const placements = pParts.length ? '\n' + pParts.join(',\n') + '\n  ' : '';
  const cues = cParts.length ? '\n' + cParts.join(',\n') + '\n  ' : '';
  return (
    '{\n' +
    `  "version": ${doc.version},\n` +
    `  "placements": [${placements}],\n` +
    `  "cues": [${cues}]\n` +
    '}\n'
  );
};

// ── audio/manifest.json (N9 — the patch editor saves through this) ─────────
// One asset per line, patch nested inline — matches the hand-curated layout.
const formatAudioAsset = (a: AudioAsset): string => {
  const parts: string[] = [`"id": ${JSON.stringify(a.id)}`, `"kind": ${JSON.stringify(a.kind ?? 'sample')}`];
  if (a.url) parts.push(`"url": ${JSON.stringify(a.url)}`);
  if (a.defaultChannel) parts.push(`"defaultChannel": ${JSON.stringify(a.defaultChannel)}`);
  if (a.loopable !== undefined) parts.push(`"loopable": ${a.loopable}`);
  if (a.license) parts.push(`"license": ${JSON.stringify(a.license)}`);
  if (a.patch) parts.push(`"patch": ${JSON.stringify(a.patch)}`);
  return `    { ${parts.join(', ')} }`;
};
const formatAudioManifest = (assets: AudioAsset[]): string =>
  '{\n  "version": 1,\n  "assets": [\n' + assets.map(formatAudioAsset).join(',\n') + '\n  ]\n}\n';

// ── atmosphere morning-shaft.json ────────────────────────────────────────
const formatAtmosphereConfig = (cfg: ReturnType<MorningShaft['getCurrentConfig']>): string => {
  const shaftLines = cfg.shafts.map((s) =>
    `    { "origin": [${s.origin.join(', ')}], "aim": [${s.aim.join(', ')}], "radius": ${s.radius} }`,
  );
  return (
    '{\n' +
    '  "shafts": [\n' + shaftLines.join(',\n') + '\n  ],\n' +
    `  "shaftIntensity": ${cfg.shaftIntensity},\n` +
    `  "fogDensity": ${cfg.fogDensity},\n` +
    `  "dustOpacity": ${cfg.dustOpacity},\n` +
    `  "dustSize": ${cfg.dustSize},\n` +
    `  "dustCount": ${cfg.dustCount}\n` +
    '}\n'
  );
};

// ── snapshot helpers ─────────────────────────────────────────────────────
export const snapshotCameraTunables = (cam: CameraMode): Record<string, number> => {
  const { target, specs } = cam.getTunables();
  const t = target as Record<string, number>;
  const out: Record<string, number> = {};
  // Round to 3dp (S7) — the rails `t` is a per-frame lerp accumulator, so saving
  // it at full float precision churned the git diff on every camera save.
  for (const s of specs) out[s.key] = r3(t[s.key]);
  return out;
};

export const applyCameraTunables = (cam: CameraMode, values: Record<string, number>): void => {
  const { target, specs } = cam.getTunables();
  const t = target as Record<string, number>;
  for (const s of specs) {
    if (typeof values[s.key] === 'number') t[s.key] = values[s.key];
  }
};

// ── Public API ───────────────────────────────────────────────────────────
/** Outcome of a soundscape save. 'conflict' = the file changed on disk since
 *  load (freshness guard refused the write); distinct from 'failed' (network /
 *  server error) so the UI can offer reload-or-force instead of plain retry. */
export type SaveResult = 'saved' | 'conflict' | 'failed';

export interface SaveFlowDeps {
  heroLookup: Map<string, Object3D>;
  morningShaft: MorningShaft;
  cameras: Record<string, CameraMode>;
  /** Source of truth for the exterior pose. Mutated by saveCameraPose() —
   *  every other save flow reads it verbatim, so saving the doorway or a
   *  bookmark never overwrites the curated outside shot. */
  exteriorPos: Vector3;
  exteriorTarget: Vector3;
  doorway: Vector3;
  interiorAABB: Box3;
  cullSettings: CullSettings;
  // DM13 — bookmarks transcend eras (no `morph`); `anchor` = a hero-local pose.
  // (heroLookup, above, is reused to compute a hero-LOCAL bookmark pose.)
  bookmarks: Record<string, { position: [number, number, number]; target: [number, number, number]; t?: number; anchor?: string }>;
  /** SHA-256 of the bytes soundscape.json was loaded from (SoundscapeDoc.baseHash).
   *  Seeds the freshness guard: saveSoundscape sends it as If-Match so a write
   *  refuses (409 → 'conflict') if the file changed on disk since load. */
  soundscapeBaseHash?: string;
  /** Snapshot of the live camera pose — position from camera.position, target
   *  from controls.target (freeform) or the forward ray (rails). Used by
   *  every save flow that writes "current view" into persisted state. */
  snapshotPose: () => { position: [number, number, number]; target: [number, number, number] };
  /** Snapshot the current Moment coordinate (time of day + era) so a saved
   *  bookmark restores the whole scene, not just the camera. */
  snapshotMoment?: () => { t: number; morph: number };
}

export interface SaveFlows {
  // Each resolves true on a successful write, false on any failure (S5) — so the
  // inspector buttons can paint an honest ✓ / ✕ instead of an optimistic flash.
  // DM5 — returns a SaveResult so a disk conflict surfaces the same persistent
  // "disk ✕ — reload or force" state as the soundscape save.
  saveHeroPositions: () => Promise<SaveResult>;
  saveShaftConfig: () => Promise<boolean>;
  saveCameraPose: () => Promise<boolean>;
  saveCurrentAsDoorway: () => Promise<boolean>;
  /** Stash the current pose under `name` in the bookmarks map and persist. */
  saveCurrentAsBookmark: (name: string) => Promise<boolean>;
  /** DM13 — stash the current pose ANCHORED to a hero (hero-local) so it still
   *  frames the hero after it moves; persist. */
  saveHeroAnchoredBookmark: (heroId: string, name: string) => Promise<boolean>;
  /** Remove a bookmark and persist. No-op if it doesn't exist. */
  deleteBookmark: (name: string) => Promise<boolean>;
  /** Persist the unified soundscape (placements + cues) to public/soundscape.json.
   *  The single audio authoring target — the legacy states.json + audio-cues.json
   *  are read-only migration fallbacks now (see soundscapeStore.loadSoundscape).
   *  Resolves a SaveResult the timeline renders (saved ✓ / disk-changed ✕ / save ✕)
   *  so saving is visible. `force` overwrites past a freshness conflict. */
  saveSoundscape: (doc: SoundscapeDoc, opts?: { force?: boolean }) => Promise<SaveResult>;
  /** Persist the audio asset registry (N9 patch edits/forks). true on 200. */
  saveAudioManifest: (assets: AudioAsset[]) => Promise<boolean>;
}

export function createSaveFlows(deps: SaveFlowDeps): SaveFlows {
  const {
    heroLookup,
    morningShaft,
    cameras,
    exteriorPos,
    exteriorTarget,
    doorway,
    interiorAABB,
    cullSettings,
    bookmarks,
    snapshotPose,
  } = deps;

  // DM5 — per-target hash of the bytes WE last wrote this session, for the
  // client-side "changed on disk since our last save" conflict guard.
  const lastSavedHash: Record<string, string> = {};

  // Builds the payload from CURRENT exteriorPos/Target (source of truth).
  // saveCameraPose mutates those vectors before calling postCameraConfig so
  // its updated values land on disk; every other save flow leaves them
  // alone and inherits the previously-saved exterior.
  const buildCameraPayload = (): CameraSavePayload => {
    const tunables: Record<string, Record<string, number>> = {};
    for (const [name, cam] of Object.entries(cameras)) {
      tunables[name] = snapshotCameraTunables(cam);
    }
    return {
      exterior: { position: r3v(exteriorPos), target: r3v(exteriorTarget) },
      doorway: r3v(doorway),
      interiorAABB: { min: r3v(interiorAABB.min), max: r3v(interiorAABB.max) },
      cull: { offset: r3(cullSettings.offset), enabled: cullSettings.enabled },
      tunables,
      bookmarks,
    };
  };

  // RMW base (S3): the bookmarks currently on disk. {} if missing/unreadable.
  // Mirrors saveHeroPositions' re-fetch so a camera save never clobbers a
  // bookmark edited on disk since boot.
  const fetchDiskBookmarks = async (): Promise<CameraSavePayload['bookmarks']> => {
    try {
      const res = await fetch('/camera/positions.json', { cache: 'no-store' });
      if (!res.ok) return {};
      const data = (await res.json()) as { bookmarks?: CameraSavePayload['bookmarks'] };
      return data.bookmarks && typeof data.bookmarks === 'object' ? data.bookmarks : {};
    } catch { return {}; }
  };

  // Every camera write re-fetches disk bookmarks, applies THIS action's delta
  // (identity for non-bookmark actions), syncs the in-memory map to the merged
  // result, then writes. The view-config fields (exterior/doorway/tunables/cull/
  // AABB) are the panel's live working set and come from memory as before — only
  // the independently-edited bookmark collection needs the read-modify-write.
  const postCameraConfig = async (
    label: string,
    bookmarkDelta: (b: CameraSavePayload['bookmarks']) => void = () => {},
  ): Promise<boolean> => {
    try {
      const fresh = await fetchDiskBookmarks();
      bookmarkDelta(fresh);
      for (const k of Object.keys(bookmarks)) delete bookmarks[k];
      Object.assign(bookmarks, fresh);
      const payload = buildCameraPayload();
      const body = formatCameraPositions(payload);
      const res = await fetch('/__lec/save-camera', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body,
      });
      if (!res.ok) throw new Error(`save responded ${res.status}: ${await res.text()}`);
      const result = (await res.json()) as { saved?: string };
      console.log(`[camera] ${label} → ${result.saved}`, payload);
      return true;
    } catch (e) {
      console.warn('[camera] save failed', e);
      return false;
    }
  };

  // Snapshot current camera pose → exterior position + target. Camera-only
  // workflow: orbit to the framing you want, hit the button. The green
  // marker follows on the next tick via cameraHandles.syncToSources.
  const saveCameraPose = async (): Promise<boolean> => {
    const p = snapshotPose();
    exteriorPos.set(p.position[0], p.position[1], p.position[2]);
    exteriorTarget.set(p.target[0], p.target[1], p.target[2]);
    return postCameraConfig('exterior pose saved');
  };

  // Snapshot current camera position → doorway. Same workflow, position-only:
  // fly the camera onto the threshold, hit the button. The cyan marker
  // follows on the next tick.
  const saveCurrentAsDoorway = async (): Promise<boolean> => {
    const p = snapshotPose().position;
    doorway.set(p[0], p[1], p[2]);
    return postCameraConfig('doorway saved');
  };

  const saveCurrentAsBookmark = async (name: string): Promise<boolean> => {
    const p = snapshotPose();
    const m = deps.snapshotMoment?.();
    const entry: CameraSavePayload['bookmarks'][string] = {
      position: p.position,
      target: p.target,
      // DM13 — keep the time of day, but NOT the era (bookmarks transcend eras).
      ...(m ? { t: r3(m.t) } : {}),
    };
    // Apply the add to the FRESH disk bookmarks (inside postCameraConfig) so a
    // concurrent on-disk edit to another bookmark survives (S3).
    return postCameraConfig(`bookmark "${name}" saved`, (b) => { b[name] = entry; });
  };

  // DM13 — a hero-ANCHORED bookmark: store the current camera pose in the hero's
  // LOCAL space so it still frames the hero after the hero is re-placed. On
  // restore (app.ts goToBookmark) the world pose is recomputed from the hero's
  // current transform. Falls back to a plain world bookmark if the hero is gone.
  const saveHeroAnchoredBookmark = async (heroId: string, name: string): Promise<boolean> => {
    const p = snapshotPose();
    const hero = deps.heroLookup.get(heroId);
    const m = deps.snapshotMoment?.();
    const entry: CameraSavePayload['bookmarks'][string] = { position: p.position, target: p.target, ...(m ? { t: r3(m.t) } : {}) };
    if (hero) {
      hero.updateWorldMatrix(true, false);
      const inv = hero.matrixWorld.clone().invert();
      const lp = new Vector3(...p.position).applyMatrix4(inv);
      const lt = new Vector3(...p.target).applyMatrix4(inv);
      entry.position = r3v(lp);
      entry.target = r3v(lt);
      entry.anchor = heroId;
    }
    return postCameraConfig(`bookmark "${name}" saved (anchored to ${heroId})`, (b) => { b[name] = entry; });
  };

  const deleteBookmark = async (name: string): Promise<boolean> => {
    if (!(name in bookmarks)) return true; // nothing to delete — vacuously fine
    return postCameraConfig(`bookmark "${name}" deleted`, (b) => { delete b[name]; });
  };

  const saveShaftConfig = async (): Promise<boolean> => {
    try {
      const config = morningShaft.getCurrentConfig();
      const body = formatAtmosphereConfig(config);
      const res = await fetch('/__lec/save-atmosphere', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body,
      });
      if (!res.ok) {
        const msg = await res.text();
        throw new Error(`save responded ${res.status}: ${msg}`);
      }
      const result = (await res.json()) as { saved?: string };
      console.log('[atmosphere] saved →', result.saved, config);
      return true;
    } catch (e) {
      console.warn('[atmosphere] save failed', e);
      return false;
    }
  };

  const saveHeroPositions = async (): Promise<SaveResult> => {
    // Strategy: load the manifest fresh, snapshot each placement's WORLD
    // transform from the live scene (so any "#all" group offset is baked
    // in), then POST the new bytes. On success, re-localize the live group
    // back to identity so subsequent drags don't compound the old offset
    // on top of the new manifest.
    //
    // Importantly: scene mutation happens AFTER the POST returns 200 — if
    // the server rejects the write, the live scene is untouched.
    try {
      const res = await fetch('/heroes/manifest.json', { cache: 'no-store' });
      if (!res.ok) throw new Error('manifest fetch: ' + res.status);
      const diskText = await res.text();
      // DM5 — client-side conflict guard: if manifest.json changed on disk since
      // OUR last write this session, refuse rather than blind-overwrite (the
      // server-side If-Match parity is the deferred "harden later" half).
      if (lastSavedHash.manifest && lastSavedHash.manifest !== cheapHash(diskText)) {
        console.warn('[manifest] save refused — manifest.json changed on disk since our last save (conflict); reload or save again to overwrite');
        return 'conflict';
      }
      const manifest = JSON.parse(diskText) as { heroes: HeroEntry[] };

      const updated: string[] = [];
      const skipped: string[] = [];
      const writePlacement = (
        placement: HeroPlacement,
        pos: Vector3,
        quat: Quaternion,
        scale: Vector3,
      ): void => {
        placement.position = [r3(pos.x), r3(pos.y), r3(pos.z)];
        const e = new Euler().setFromQuaternion(quat);
        const ax = r3(e.x), ay = r3(e.y), az = r3(e.z);
        if (ax === 0 && ay === 0 && az === 0) delete placement.rotation;
        else placement.rotation = [ax, ay, az];
        const sx = r3(scale.x), sy = r3(scale.y), sz = r3(scale.z);
        if (sx === 1 && sy === 1 && sz === 1) delete placement.scale;
        else if (sx === sy && sy === sz) placement.scale = sx;
        else placement.scale = [sx, sy, sz];
      };

      // Pass 1: snapshot world transforms BEFORE mutating anything. Done
      // for every entry so we have a complete picture before POST.
      const allSnaps: Array<{
        entry: HeroEntry;
        multi: boolean;
        snaps: Array<{
          instanceId: string;
          obj: Object3D | undefined;
          pos: Vector3;
          quat: Quaternion;
          scale: Vector3;
        }>;
      }> = [];
      for (const entry of manifest.heroes ?? []) {
        const placements = entry.placements ?? [];
        const multi = placements.length > 1;
        const snaps = placements.map((_, i) => {
          const instanceId = multi ? `${entry.id}#${i}` : entry.id;
          const obj = heroLookup.get(instanceId);
          const pos = new Vector3();
          const quat = new Quaternion();
          const scale = new Vector3();
          if (obj) {
            obj.getWorldPosition(pos);
            obj.getWorldQuaternion(quat);
            obj.getWorldScale(scale);
          }
          return { instanceId, obj, pos, quat, scale };
        });
        allSnaps.push({ entry, multi, snaps });
      }

      // Pass 2: write snapshots into the manifest data structure.
      for (const { entry, snaps } of allSnaps) {
        const placements = entry.placements ?? [];
        for (let i = 0; i < placements.length; i++) {
          const snap = snaps[i];
          if (!snap.obj) {
            skipped.push(snap.instanceId + ' (not in scene)');
            continue;
          }
          writePlacement(placements[i], snap.pos, snap.quat, snap.scale);
          const liveState = snap.obj.userData.state;
          if (liveState === 'past' || liveState === 'present' || liveState === 'both') {
            placements[i].state = liveState;
          }
          updated.push(snap.instanceId);
        }
      }

      // Pass 3: POST. If this fails we abort — the live scene is still
      // intact, the manifest on disk is still the previous version.
      const saveRes = await fetch('/__lec/save-manifest', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: formatManifest(manifest),
      });
      if (!saveRes.ok) {
        const msg = await saveRes.text();
        throw new Error(`save responded ${saveRes.status}: ${msg}`);
      }
      const result = (await saveRes.json()) as { saved?: string };

      // Pass 4: NOW it's safe to normalize the live scene. Group back to
      // identity, children to the captured world transforms, so the
      // manifest stays the single source of truth for what's on screen
      // and a future drag-the-group starts from a clean offset.
      for (const { entry, multi, snaps } of allSnaps) {
        if (!multi) continue;
        const group = heroLookup.get(entry.id + '#all');
        if (!group) continue;
        group.position.set(0, 0, 0);
        group.quaternion.identity();
        group.scale.set(1, 1, 1);
        for (const snap of snaps) {
          if (!snap.obj) continue;
          snap.obj.position.copy(snap.pos);
          snap.obj.quaternion.copy(snap.quat);
          snap.obj.scale.copy(snap.scale);
        }
      }

      console.log('[manifest] saved →', result.saved);
      console.log('[manifest] updated:', updated);
      if (skipped.length) console.log('[manifest] skipped:', skipped);
      lastSavedHash.manifest = cheapHash(formatManifest(manifest)); // our bytes are now disk truth
      return 'saved';
    } catch (e) {
      console.warn('[manifest] save failed', e);
      return 'failed';
    }
  };

  // Hash of the bytes believed to be on disk — seeded from the boot load,
  // advanced to the server's echo after each successful write. Sent as If-Match
  // so a write refuses (409) when the file changed on disk since (S1).
  let soundscapeHash = deps.soundscapeBaseHash;

  const saveSoundscape = async (doc: SoundscapeDoc, opts?: { force?: boolean }): Promise<SaveResult> => {
    try {
      const body = formatSoundscape(doc);
      const headers: Record<string, string> = { 'content-type': 'application/json' };
      // force → If-Match:* (overwrite regardless); otherwise send the loaded
      // hash if we have one (no header on a fallback load = nothing to match).
      if (opts?.force) headers['if-match'] = '*';
      else if (soundscapeHash) headers['if-match'] = soundscapeHash;
      const res = await fetch('/__lec/save-soundscape', { method: 'POST', headers, body });
      if (res.status === 409) {
        // Deliberately do NOT adopt the disk hash here — keep refusing ordinary
        // saves until the user reloads (takes disk) or forces (overwrites it).
        // Adopting it would let the very next ⌘S match disk and silently clobber
        // the change we just warned about.
        console.warn('[soundscape] save refused — soundscape.json changed on disk since load (409); reload or force');
        return 'conflict';
      }
      if (!res.ok) throw new Error(`save responded ${res.status}: ${await res.text()}`);
      const result = (await res.json()) as { saved?: string; hash?: string };
      if (result.hash) soundscapeHash = result.hash; // our bytes are now the on-disk truth
      console.log('[soundscape] saved →', result.saved, doc);
      return 'saved';
    } catch (e) {
      // Returns a result (not throw) so the caller surfaces a visible state
      // without an uncaught rejection. The timeline's save buttons reflect it.
      console.warn('[soundscape] save failed', e);
      return 'failed';
    }
  };

  const saveAudioManifest = async (assets: AudioAsset[]): Promise<boolean> => {
    try {
      const res = await fetch('/__lec/save-audio-manifest', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: formatAudioManifest(assets),
      });
      if (!res.ok) throw new Error(`save responded ${res.status}: ${await res.text()}`);
      console.log('[audio manifest] saved —', assets.length, 'assets');
      return true;
    } catch (e) {
      console.warn('[audio manifest] save failed', e);
      return false;
    }
  };

  return {
    saveHeroPositions,
    saveShaftConfig,
    saveCameraPose,
    saveCurrentAsDoorway,
    saveCurrentAsBookmark,
    saveHeroAnchoredBookmark,
    deleteBookmark,
    saveSoundscape,
    saveAudioManifest,
  };
}
